import { DART_APEX, DART_LEFT, DART_RIGHT, MIRROR_START, MIRROR_END } from '../constants/knotTypes';
import MathHelper from './MathHelper';

const TENSION = 0.4;

export function createKnot(id, x, y, corner = true, lH = { x: NaN, y: NaN }, rH = { x: NaN, y: NaN }, grades = []) {
  const lHI = Number.isNaN(lH.x) || Number.isNaN(lH.y);
  const rHI = Number.isNaN(rH.x) || Number.isNaN(rH.y);
  return {
    id,
    x,
    y,
    lH,
    rH,
    lHI,
    rHI,
    corner,
    grades
  };
}

/**
 * Calculate relative handles
 */
export function calculateKnotHandles(knot, handlePoint) {
  const x = handlePoint.x - knot.x;
  const y = handlePoint.y - knot.y;

  return { lH: { x: -x, y: -y }, rH: { x, y } };
}

/**
 * @return {boolean}
 */
export function LHDefined(knot) {
  return !knot.lHI && !Number.isNaN(knot.lH.x) && !Number.isNaN(knot.lH.y);
}

/**
 * @return {boolean}
 */
export function RHDefined(knot) {
  return !knot.rHI && !Number.isNaN(knot.rH.x) && !Number.isNaN(knot.rH.y);
}

export function RHIDefined(knot) {
  return knot.rHI && !Number.isNaN(knot.rH.x) && !Number.isNaN(knot.rH.y);
}

export function LHIDefined(knot) {
  return knot.lHI && !Number.isNaN(knot.lH.x) && !Number.isNaN(knot.lH.y);
}

/**
 * @return {boolean}
 */
export function HandlesDefined(knot) {
  return LHDefined(knot) && RHDefined(knot);
}

export function GetRH(knot) {
  return !knot.rHI ? knot.rH : null;
}

export function GetLH(knot) {
  return !knot.lHI ? knot.lH : null;
}

export function GetRHI(knot) {
  return knot.rHI ? knot.rH : null;
}

export function GetLHI(knot) {
  return knot.lHI ? knot.lH : null;
}

export function GetRelRH(knot) {
  return RHDefined(knot) || RHIDefined(knot) ? knot.rH : null;
}

export function GetRelLH(knot) {
  return LHDefined(knot) || LHIDefined(knot) ? knot.lH : null;
}

export function GetAbsRH(knot) {
  if (GetRelRH(knot)) {
    return MathHelper.addPoints(knot, GetRelRH(knot));
  }
  return null;
}

export function GetAbsLH(knot) {
  if (GetRelLH(knot)) {
    return MathHelper.addPoints(knot, GetRelLH(knot));
  }
  return null;
}

/*
 * Sets rhx and rhy relative to the knot, takes the knot and the absolute point
 */
export function SetRH(knot, point) {
  const rH = MathHelper.substractPoints(point, knot);

  if (!knot.corner) {
    const angle = MathHelper.getAnglePoints(knot, MathHelper.addPoints(knot, rH));
    const distance = MathHelper.getDistance(knot, GetAbsLH(knot));

    return {
      ...knot,
      rH,
      rHI: false,
      lHI: false,
      lH: {
        x: -MathHelper.getAdjacentLength(distance, angle),
        y: -MathHelper.getOppositeLength(distance, angle),
      },
    };
  }

  return {
    ...knot,
    rHI: false,
    rH,
  };
}

export function getBounds(knot) {
  const propsKnot = knot[0];

  const xMax = Math.max((propsKnot.x + propsKnot.lH.x), (propsKnot.x + propsKnot.rH.x), propsKnot.x);
  const xMin = Math.min((propsKnot.x + propsKnot.lH.x), (propsKnot.x + propsKnot.rH.x), propsKnot.x);
  const yMax = Math.max((propsKnot.y + propsKnot.lH.y), (propsKnot.y + propsKnot.rH.y), propsKnot.y);
  const yMin = Math.min((propsKnot.y + propsKnot.lH.y), (propsKnot.y + propsKnot.rH.y), propsKnot.y);

  const bounds = { x: xMin, y: yMin, w: (xMax - xMin), h: (yMax - yMin) };

  return bounds;
}

export function getKnotAndHandles(piece, knotSelection) {
  const { knots } = piece;
  const knotSelected = knots.filter((knot) => knot.id === knotSelection);

  return knotSelected;
}

export function SetLH(knot, point) {
  const lH = MathHelper.substractPoints(point, knot);

  if (!knot.corner) {
    const angle = MathHelper.getAnglePoints(knot, MathHelper.addPoints(knot, lH));
    const distance = MathHelper.getDistance(knot, GetAbsRH(knot));

    return {
      ...knot,
      lH,
      lHI: false,
      rHI: false,
      rH: {
        x: -MathHelper.getAdjacentLength(distance, angle),
        y: -MathHelper.getOppositeLength(distance, angle),
      },
    };
  }

  return {
    ...knot,
    lH,
    lHI: false,
  };
}

export function setHandle(knot, direction, point) {
  return (direction === 'LEFT') ? SetLH(knot, point) : SetRH(knot, point);
}

export function clearHandle(knot, direction) {
  if (direction === 'LEFT') {
    return {
      ...knot,
      lH: { x: NaN, y: NaN },
      lHI: true,
    };
  }

  return {
    ...knot,
    rH: { x: NaN, y: NaN },
    rHI: true,
  };
}

export function clearHandles(knot) {
  return {
    ...knot,
    lHI: false,
    rHI: false,
  };
}

export function setCorner(knot, corner) {
  if (knot.corner === corner) {
    return knot;
  }

  return {
    ...clearHandles(knot),
    corner,
  };
}

export function getAngleBetweenHandles(knot) {
  const lH = GetRelLH(knot);
  const rH = GetRelRH(knot);

  if (!lH || !rH) return null;

  return MathHelper.getAngle(lH.x, lH.y) - MathHelper.getAngle(rH.x, rH.y);
}

export function areAnglesAligned(knot) {
  let angle = getAngleBetweenHandles(knot);

  if (angle === null) return false;

  const threshold = MathHelper.convertDegreesToRadians(15);

  angle = MathHelper.getOneTurnPositiveAngle(angle);

  return (Math.PI - angle) > threshold;
}

export function ChangeKnotPointToAbsolute(knot) {
  const { x, y } = knot;
  const lhA = GetAbsLH(knot) ? GetAbsLH(knot) : { x: NaN, y: NaN };
  const rhA = GetAbsRH(knot) ? GetAbsRH(knot) : { x: NaN, y: NaN };
  return { x, y, lhAx: lhA.x, lhAy: lhA.y, rhAx: rhA.x, rhAy: rhA.y };
}

export function InterpolateSmoothHandles(knot, prev, next) {
  if (HandlesDefined(knot) || knot.corner || !prev || !next) {
    return knot;
  }

  const distToPrev = MathHelper.getDistance(knot, prev);
  const distToNext = MathHelper.getDistance(knot, next);
  const leftRatio = (TENSION * distToPrev) / (distToPrev + distToNext);
  const rightRatio = TENSION - leftRatio;

  const lH = {
    x: leftRatio * (prev.x - next.x),
    y: leftRatio * (prev.y - next.y),
  };

  const rH = {
    x: -rightRatio * (prev.x - next.x),
    y: -rightRatio * (prev.y - next.y),
  };

  return {
    ...knot,
    lH,
    lHI: true,
    rH,
    rHI: true,
  };
}

export function immutableInterpolateSmoothHandles(knot, prev, next) {
  const jsKnot = knot.toJS();

  if (HandlesDefined(jsKnot) || jsKnot.corner || !prev || !next) return knot;

  const [prevPosition, nextPosition] = [prev, next].map((k) => ({ x: k.get('x'), y: k.get('y') }));

  const distToPrev = MathHelper.getDistance(jsKnot, prevPosition);
  const distToNext = MathHelper.getDistance(jsKnot, nextPosition);
  const leftRatio = (TENSION * distToPrev) / (distToPrev + distToNext);
  const rightRatio = TENSION - leftRatio;

  const lH = {
    x: leftRatio * (prev.get('x') - next.get('x')),
    y: leftRatio * (prev.get('y') - next.get('y')),
  };

  const rH = {
    x: -rightRatio * (prev.get('x') - next.get('x')),
    y: -rightRatio * (prev.get('y') - next.get('y')),
  };

  return knot.set('lH', knot.get('lH').set('x', lH.x).set('y', lH.y))
    .set('lHI', true)
    .set('rH', knot.get('rH').set('x', rH.x).set('y', rH.y))
    .set('rHI', true);
}

export function InterpolateHandles(knot, prev, next) {
  if (HandlesDefined(knot) || !knot.corner) {
    return knot;
  }

  let { rH, lH, rHI, lHI } = knot;

  if (knot.rHI && next) {
    rHI = true;

    if (LHDefined(next) || (!next.corner && LHIDefined(next))) {
      rH = MathHelper.interpolatePoints(knot, GetAbsLH(next), TENSION);
    } else {
      rH = MathHelper.interpolatePoints(knot, next, 0.3);
    }
  }

  if (knot.lHI && prev) {
    lHI = true;

    if (RHDefined(prev) || (!prev.corner && RHIDefined(prev))) {
      lH = MathHelper.interpolatePoints(knot, GetAbsRH(prev), TENSION);
    } else {
      lH = MathHelper.interpolatePoints(knot, prev, 0.3);
    }
  }

  return {
    ...knot,
    lH,
    lHI,
    rH,
    rHI,
  };
}

export function immutableInterpolateHandles(knot, prev, next) {
  const jsKnot = knot.toJS();

  if (HandlesDefined(jsKnot) || !jsKnot.corner) return knot;

  let newKnot = knot;

  if (jsKnot.rHI && next) {
    const jsNext = next.toJS();

    newKnot = newKnot.set('rHI', true);

    let newRH = MathHelper.interpolatePoints(jsKnot, jsNext, 0.3);

    if (LHDefined(jsNext) || (!jsNext.corner && LHIDefined(jsNext))) {
      newRH = MathHelper.interpolatePoints(jsKnot, GetAbsLH(jsNext), TENSION);
    }

    newKnot = newKnot.set('rH', newKnot.get('rH').set('x', newRH.x).set('y', newRH.y));
  }

  if (jsKnot.lHI && prev) {
    const jsPrev = prev.toJS();

    newKnot = newKnot.set('lHI', true);

    let newLH = MathHelper.interpolatePoints(jsKnot, jsPrev, 0.3);

    if (RHDefined(jsPrev) || (!jsPrev.corner && RHIDefined(jsPrev))) {
      newLH = MathHelper.interpolatePoints(jsKnot, GetAbsRH(jsPrev), TENSION);
    }

    newKnot = newKnot.set('lH', newKnot.get('lH').set('x', newLH.x).set('y', newLH.y));
  }

  return newKnot;
}

export function FlipHorizontal(knot, xAxis) {
  let { lH, rH } = knot;

  if (LHDefined(knot)) {
    lH = { ...lH, x: -lH.x };
  }
  if (RHDefined(knot)) {
    rH = { ...rH, x: -rH.x };
  }

  return {
    ...knot,
    x: xAxis + (xAxis - knot.x),
    lH,
    rH,
  };
}

export function FlipVertical(knot, yAxis) {
  let { lH, rH } = knot;

  if (LHDefined(knot)) {
    lH = { ...lH, y: -lH.y };
  }
  if (RHDefined(knot)) {
    rH = { ...rH, y: -rH.y };
  }

  return {
    ...knot,
    y: yAxis + (yAxis - knot.y),
    lH,
    rH,
  };
}

export function Reverse(knot) {
  return {
    ...knot,
    rH: knot.lH,
    rHI: knot.lHI,
    lH: knot.rH,
    lHI: knot.rHI,
  };
}

export function getInverseType(knot) {
  switch (knot.type) {
    case DART_LEFT: {
      return DART_RIGHT;
    }
    case DART_RIGHT: {
      return DART_LEFT;
    }
    case MIRROR_START: {
      return MIRROR_END;
    }
    case MIRROR_END: {
      return MIRROR_START;
    }
    default: {
      return knot.type;
    }
  }
}

export function isKnotInRectangle(knot, rectangle) {
  const { x, y, width, height } = rectangle;

  if (knot.x >= x && knot.x <= x + width && knot.y >= y && knot.y <= y + height) {
    return true;
  }

  return false;
}

export function isDart(knot) {
  const { type } = knot;
  return type && (type === DART_APEX || type === DART_LEFT || type === DART_RIGHT);
}

export function isDartApex(knot) {
  const { type } = knot;
  return type && (type === DART_APEX);
}

export function reflectKnot(knot, axisStart, axisEnd, invert = true) {
  const { lHI, rHI, grades } = knot;
  let { type } = knot;
  const { x, y } = MathHelper.reflectPoint(knot, axisStart, axisEnd);

  if (type === DART_LEFT) type = DART_RIGHT;
  else if (type === DART_RIGHT) type = DART_LEFT;

  let lH = { x: NaN, y: NaN };
  let rH = { x: NaN, y: NaN };

  let reflectedHandle;

  if (!lHI) {
    reflectedHandle = MathHelper.reflectPoint(invert ? GetAbsLH(knot) : GetAbsRH(knot), axisStart, axisEnd);
    rH = MathHelper.substractPoints(reflectedHandle, { x, y });
  }

  if (!rHI) {
    reflectedHandle = MathHelper.reflectPoint(invert ? GetAbsRH(knot) : GetAbsLH(knot), axisStart, axisEnd);
    lH = MathHelper.substractPoints(reflectedHandle, { x, y });
  }

  let newGrades = [];
  if ( grades ) {
    grades.forEach(function (g, i) {
      if (!g) return;
      let absGrade = MathHelper.addPoints(knot, g);
      let reflectedGrade = MathHelper.reflectPoint(absGrade, axisStart, axisEnd);
      newGrades[i] = MathHelper.substractPoints(reflectedGrade, { x, y });
    });
  }

  return { ...knot, x, y, lH, rH, lHI: rHI, rHI: lHI, type, grades:newGrades };
}

export function scaleHandles(knot, scaleLeft, scaleRight) {
  const lH = GetRelLH(knot);
  const rH = GetRelRH(knot);
  return {
    ...knot,
    lH: lH ? MathHelper.scalePoint(lH, scaleLeft) : null,
    rH: rH ? MathHelper.scalePoint(rH, scaleRight) : null,
  };
}
